import React, { useState, useEffect } from 'react';
import { useUserProfile } from '../../hooks/useUserProfile';
import enhancedApiClient from '../../api/enhancedApiClient';

const AddTaskModal = ({ isOpen, onClose, onTaskAdded }) => {
  const [formData, setFormData] = useState({
    name: '',
    details: '',
    dueDate: '',
    eventId: '',
    attachment: null,
    backgroundImage: null
  });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [currentUser, setCurrentUser] = useState(null);
  
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const { Auth } = await import('aws-amplify');
        const user = await Auth.currentAuthenticatedUser();
        setCurrentUser(user);
      } catch (error) {
        console.log('No authenticated user');
      }
    };
    getCurrentUser();
  }, []);
  
  const { userProfile } = useUserProfile(currentUser?.username);
  const vendorId = userProfile?.currentVendorId;

  useEffect(() => {
    const fetchEvents = async () => {
      if (!vendorId) return;
      
      try {
        const response = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/vendors/${vendorId}/events`);
        const eventsData = response.ok ? await response.json() : [];
        setEvents(eventsData);
        
        // Set first event as default
        if (eventsData.length > 0) {
          setFormData(prev => ({ ...prev, eventId: eventsData[0].id }));
        }
      } catch (error) {
        console.error('Error fetching events:', error);
      }
    };

    if (isOpen && vendorId) {
      fetchEvents();
    }
  }, [isOpen, vendorId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      let documentUrl = null;
      let backgroundImageUrl = null;
      
      // Upload attachment file to S3 if exists
      if (formData.attachment) {
        const uploadResult = await enhancedApiClient.uploadFile(formData.attachment);
        documentUrl = uploadResult.fileUrl;
      }
      
      // Upload background image to S3 if exists
      if (formData.backgroundImage) {
        const uploadResult = await enhancedApiClient.uploadFile(formData.backgroundImage);
        backgroundImageUrl = uploadResult.fileUrl;
      }
      
      // Create Task record
      await enhancedApiClient.createTask({
        eventId: formData.eventId,
        name: formData.name,
        details: formData.details,
        dueDate: formData.dueDate,
        status: 'pending',
        vendorId: vendorId,
        documentUrl: documentUrl,
        backgroundImageUrl: backgroundImageUrl,
        createdByUserId: currentUser?.username,
        createdAt: new Date().toISOString()
      });
      
      setFormData({ name: '', details: '', dueDate: '', eventId: events[0]?.id || '', attachment: null, backgroundImage: null });
      onTaskAdded?.();
      onClose();
    } catch (error) {
      console.error('Error creating task:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        width: '100%',
        maxWidth: '500px',
        margin: '20px'
      }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: '600',
          color: '#1F2937',
          marginBottom: '20px',
          fontFamily: 'Georgia, serif'
        }}>Add Task</h2>
        
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '6px'
            }}>Event</label>
            <select
              value={formData.eventId}
              onChange={(e) => setFormData({...formData, eventId: e.target.value})}
              required
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '16px',
                backgroundColor: '#F9FAFB',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#6B9080'}
              onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
            >
              {events.sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(event => (
                <option key={event.id} value={event.id}>
                  {event.name}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '6px'
            }}>Task Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '16px',
                backgroundColor: '#F9FAFB',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#6B9080'}
              onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '6px'
            }}>Due Date</label>
            <input
              type="date"
              value={formData.dueDate}
              onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
              required
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '16px',
                backgroundColor: '#F9FAFB',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#6B9080'}
              onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '6px'
            }}>Details</label>
            <textarea
              value={formData.details}
              onChange={(e) => setFormData({...formData, details: e.target.value})}
              rows={3}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '16px',
                backgroundColor: '#F9FAFB',
                resize: 'vertical',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#6B9080'}
              onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '6px'
            }}>Background Image <span style={{ color: '#9CA3AF' }}>(Optional)</span></label>
            <div style={{ position: 'relative' }}>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file && file.size > 5 * 1024 * 1024) {
                    alert('Image size must be less than 5MB');
                    e.target.value = '';
                    return;
                  }
                  setFormData({...formData, backgroundImage: file});
                }}
                style={{
                  position: 'absolute',
                  opacity: 0,
                  width: '100%',
                  height: '100%',
                  cursor: 'pointer'
                }}
                id="background-image-upload"
              />
              <label
                htmlFor="background-image-upload"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #6B9080',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#6B9080',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#6B9080';
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.color = '#6B9080';
                }}
              >
                {formData.backgroundImage ? formData.backgroundImage.name : 'Choose Image'}
              </label>
            </div>
            {formData.backgroundImage && (
              <div style={{ marginTop: '12px', position: 'relative' }}>
                <img 
                  src={URL.createObjectURL(formData.backgroundImage)} 
                  alt="Preview" 
                  style={{
                    width: '100%',
                    height: '120px',
                    objectFit: 'cover',
                    borderRadius: '8px'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setFormData({...formData, backgroundImage: null})}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ×
                </button>
              </div>
            )}
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#6B7280' }}>
              Max 5MB. This image will appear on the task card in the client dashboard.
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '6px'
            }}>Attachment <span style={{ color: '#9CA3AF' }}>(Optional)</span></label>
            <div style={{ position: 'relative' }}>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file && file.size > 10 * 1024 * 1024) {
                    alert('File size must be less than 10MB');
                    e.target.value = '';
                    return;
                  }
                  setFormData({...formData, attachment: file});
                }}
                style={{
                  position: 'absolute',
                  opacity: 0,
                  width: '100%',
                  height: '100%',
                  cursor: 'pointer'
                }}
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #6B9080',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#6B9080',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#6B9080';
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.color = '#6B9080';
                }}
              >
                {formData.attachment ? formData.attachment.name : 'Choose File'}
              </label>
            </div>
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#6B7280' }}>
              Max 10MB. PDF, DOC, DOCX, JPG, or PNG
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '12px 24px',
                backgroundColor: 'white',
                color: '#6B7280',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '12px 24px',
                backgroundColor: loading ? '#9CA3AF' : '#6B9080',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Adding...' : 'Add Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddTaskModal;